<script setup lang="ts">
// The season against its history: this season in blue over the five seasons
// before it, every line smoothed with the same 3-week average and drawn on the
// same disease-week axis. The faint dots are this season's raw weekly values,
// because the smoothing is the reading and the raw weeks are the evidence under
// it.
import { computed } from "vue";
import type { ChartDataset } from "chart.js";

import type { MapReviewPopulationSurveillance } from "@/type/api";

import LineChart from "../charts/LineChart.vue";
import { MUTED_COLOR, OKABE_ITO } from "../charts/theme";

const props = defineProps<{
	surveillance: MapReviewPopulationSurveillance;
}>();

/** Five muted tones, one per prior season, so overlapping history stays
 * countable. They are deliberately quieter than the current season's hue. */
const PRIOR_TONES = ["#9aa3ad", "#b0a58f", "#8fa7a0", "#a99aa8", "#a3a3a3"];
const RAW_COLOR = "#b6bdc6";

const hasWeeks = computed(() => props.surveillance.weeks.length > 0);
const labels = computed(() =>
	props.surveillance.weeks.map((week) => `W${week}`),
);

/** The current season's year: the calendar year after the last prior season. */
const currentYear = computed(() => {
	const prior = props.surveillance.prior;
	return prior.length > 0 ? prior[prior.length - 1].year + 1 : null;
});

const currentName = computed(() =>
	currentYear.value === null ? "This season" : String(currentYear.value),
);

const priorName = computed(() => {
	const prior = props.surveillance.prior;
	if (prior.length === 0) return "Prior seasons";
	return `Prior seasons ${prior[0].year}–${prior[prior.length - 1].year}`;
});

const datasets = computed<ChartDataset<"line", (number | null)[]>[]>(() => {
	const view = props.surveillance;
	const rows: ChartDataset<"line", (number | null)[]>[] = view.prior.map(
		(season, index) => ({
			label: String(season.year),
			data: season.smoothed,
			borderColor: PRIOR_TONES[index % PRIOR_TONES.length],
			backgroundColor: "transparent",
			borderWidth: 1.2,
			pointRadius: 0,
			pointHoverRadius: 2,
			tension: 0.3,
			fill: false,
		}),
	);
	// The raw weeks are points only: no line joins them, because the weekly
	// trap catch is noisy and joining it would draw a shape the smoothing
	// exists to remove.
	rows.push({
		label: `Raw weekly, ${currentName.value}`,
		data: view.current.raw,
		borderColor: "transparent",
		backgroundColor: RAW_COLOR,
		borderWidth: 0,
		pointRadius: 1.2,
		pointHoverRadius: 3,
		showLine: false,
		fill: false,
	});
	rows.push({
		label: `${currentName.value} (smoothed)`,
		data: view.current.smoothed,
		borderColor: OKABE_ITO[0],
		backgroundColor: "transparent",
		borderWidth: 2.4,
		pointRadius: 0,
		pointHoverRadius: 4,
		tension: 0.3,
		fill: false,
	});
	return rows;
});
</script>

<template>
	<section class="population-seasons">
		<p class="panel-title">Catch per trap, by disease week</p>
		<p class="panel-help">
			This season in blue over the five seasons before it, all smoothed with the
			same 3-week average. The history replays the current filter: the same
			stations, the same species and sex, the same weeks.
		</p>
		<p v-if="!hasWeeks" class="panel-empty">
			No disease week carries an analysed sample under this filter.
		</p>
		<div v-else class="panel-chart">
			<LineChart :labels="labels" :datasets="datasets" :show-legend="false" />
		</div>
		<div v-if="hasWeeks" class="legend">
			<span class="key"
				><span class="swatch" :style="{ background: OKABE_ITO[0] }" />{{
					currentName
				}}
				(smoothed)</span
			>
			<span class="key"
				><span class="swatch dot" :style="{ background: RAW_COLOR }" />Raw
				weekly</span
			>
			<span class="key"
				><span class="swatch" :style="{ background: MUTED_COLOR }" />{{
					priorName
				}}</span
			>
		</div>
	</section>
</template>

<style scoped>
.population-seasons {
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
	min-width: 0;
	/* The pane's slot is a cell grid; this panel reads as one column of the
	   rail, in the order the question asks. */
	grid-column: 1 / -1;
}

.panel-title {
	margin: 0 0 2px;
	font-size: 0.78rem;
	font-weight: 600;
	color: #212529;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.panel-chart {
	position: relative;
	height: 190px;
}

.legend {
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	margin-top: 6px;
	font-size: 0.66rem;
	color: #6c757d;
}

.key {
	display: flex;
	align-items: center;
	gap: 5px;
}

.swatch {
	width: 14px;
	height: 3px;
	border-radius: 2px;
}

.swatch.dot {
	width: 5px;
	height: 5px;
	border-radius: 50%;
}
</style>
